import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Description as PageIcon,
  Schedule as HoursIcon,
  ContactMail as ContactIcon,
  Info as AboutIcon,
  Edit as EditIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { IconButton, Tooltip, Switch } from '@mui/material';

import WebsitePageWrapper from './WebsitePageWrapper';
import { useStoreWebsite } from '../hooks/useStoreWebsite';

const getPageIcon = (pageType) => {
  switch (pageType) {
    case 'about':
      return <AboutIcon />;
    case 'contact':
      return <ContactIcon />;
    case 'hours':
      return <HoursIcon />;
    default:
      return <PageIcon />;
  }
};

const getPageDescription = (pageType) => {
  switch (pageType) {
    case 'about':
      return 'Tell customers about your store';
    case 'contact':
      return 'Contact information and location';
    case 'hours':
      return 'Store operating hours';
    default:
      return 'Custom page';
  }
};

const WebsitePagesPage = ({ userOrganizationId }) => {
  return (
    <WebsitePageWrapper
      title="Website Pages"
      subtitle="Manage the content on your public website"
      userOrganizationId={userOrganizationId}
    >
      {PagesContent}
    </WebsitePageWrapper>
  );
};

const PagesContent = ({ website, pages, locationId, publicUrl }) => {
  const navigate = useNavigate();
  const { togglePagePublish, isSaving } = useStoreWebsite(locationId);

  const handleEditPage = (page) => {
    navigate(`/website/pages/${page._id}?location=${locationId}`);
  };

  const handlePreview = (pageId) => {
    if (publicUrl) {
      window.open(`${publicUrl}/${pageId}`, '_blank');
    }
  };

  const handleTogglePublish = async (pageId) => {
    await togglePagePublish(pageId);
  };

  // Sort pages: standard pages first (about, contact, hours), then custom
  const sortedPages = [...pages].sort((a, b) => {
    const order = { about: 1, contact: 2, hours: 3 };
    const orderA = order[a.page_type] || 99;
    const orderB = order[b.page_type] || 99;
    return orderA - orderB;
  });

  return (
    <>
      <div className="website-page-content">
        <div className="website-card">
          <div className="website-card-header">
            <h2 className="website-card-title">Website Pages</h2>
            <p className="website-card-subtitle">
              Manage the content on your public website. Edit each page to add your store's information.
            </p>
          </div>

          <div className="website-pages-list">
            {sortedPages.map((page) => (
              <div key={page._id} className="website-page-item">
                <div className="website-page-item-left">
                  <div className="website-page-item-icon">
                    {getPageIcon(page.page_type)}
                  </div>
                  <div className="website-page-item-info">
                    <h3 className="website-page-item-title">{page.title}</h3>
                    <p className="website-page-item-desc">{getPageDescription(page.page_type)}</p>
                  </div>
                </div>

                <div className="website-page-item-right">
                  <div className="website-page-item-toggle">
                    <Switch
                      checked={page.is_published}
                      onChange={() => handleTogglePublish(page._id)}
                      disabled={isSaving}
                      size="small"
                    />
                    <span className={`website-page-item-status ${page.is_published ? 'published' : ''}`}>
                      Published
                    </span>
                  </div>

                  <div className="website-page-item-actions">
                    <Tooltip title="Preview">
                      <IconButton
                        onClick={() => handlePreview(page._id)}
                        size="small"
                        className="website-action-btn"
                      >
                        <VisibilityIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                      <IconButton
                        onClick={() => handleEditPage(page)}
                        size="small"
                        className="website-action-btn primary"
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {pages.length === 0 && (
            <div className="website-empty-message">
              <p>No pages found. Default pages will be created when you set up your website.</p>
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="website-card info-card">
          <div className="website-card-header">
            <div className="website-card-title-with-icon">
              <AboutIcon className="website-card-title-icon" />
              <h2 className="website-card-title">About Your Website</h2>
            </div>
          </div>
          <div className="website-card-body">
            <p className="website-info-paragraph">
              Your public website is automatically generated and hosted by adgro.
              Use these pages to share information about your store, contact details, and operating hours with your customers.
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

export default WebsitePagesPage;
